import { yupResolver } from '@hookform/resolvers/yup';
import { BulkSms_Create } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Order } from '~hooks/useOrders/models';
import { useOrders } from '~hooks/useOrders/useOrders';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

enum SendTextDialogMode {
  BULK_DRIVERS = 'bulk_drivers',
  JOB_DRIVER = 'job_driver',
  ORDER_DRIVERS = 'order_drivers',
}

type FunctionSendSms = (jobId: string, message: string) => Promise<void>;
type FunctionBulkSendSms = (args: BulkSms_Create) => Promise<void>;

type PropsByMode = {
  canSendSms: boolean;
  sendSms: FunctionSendSms | FunctionBulkSendSms;
  sourceFriendlyId?: string;
  textFieldPlaceholder: string;
};

interface SendTextDialogProps {
  isOpen: boolean;
  mode: `${SendTextDialogMode}`;
  onClose: () => void;
  source?: Job | Order | string[];
}

const sendTextSchema = yup.object().shape({
  message: yup
    .string()
    .trim()
    .min(1, t('form_validation_errors.required', { field: t('form_fields.message') }))
    .required(),
});

type SendTextDTO = yup.InferType<typeof sendTextSchema>;

export default function SendTextDialog({
  isOpen,
  mode,
  onClose,
  source,
}: SendTextDialogProps) {
  const {
    bulkSendSmsToDrivers,
    sendSmsToDriver,
    isSendingSms: isSendingSmsToJobDriver,
  } = useJob();
  const { sendSmsToAllDrivers } = useOrders();
  const { toasterStore } = useStores();
  const [isSendingSmsToOrderDrivers, setIsSendingSmsToOrderDrivers] = useState(false);

  const isSendingSms = isSendingSmsToJobDriver || isSendingSmsToOrderDrivers;
  const propsByMode = useMemo(() => {
    if (!source) {
      return null;
    }

    if (mode === SendTextDialogMode.JOB_DRIVER) {
      const job = source as Job;

      const propsByMode: PropsByMode = {
        canSendSms: Boolean(job.driver?.phone),
        sendSms: sendSmsToDriver,
        sourceFriendlyId: job.jobId,
        textFieldPlaceholder: t('dispatch.job.send_text.send_message_to', {
          name: String(job.driver?.fullName),
        }),
      };

      return propsByMode;
    }

    if (mode === SendTextDialogMode.ORDER_DRIVERS) {
      const order = source as Order;

      const propsByMode: PropsByMode = {
        canSendSms: Boolean(order.orderSummary?.jobsCount),
        sendSms: sendSmsToAllDrivers,
        sourceFriendlyId: order.orderId,
        textFieldPlaceholder: t('dispatch.job.send_text.send_message_to', {
          name: t('dispatch.job.send_text.all_drivers'),
        }),
      };

      return propsByMode;
    }

    const propsByMode: PropsByMode = {
      canSendSms: true,
      sendSms: bulkSendSmsToDrivers,
      textFieldPlaceholder: t('dispatch.job.send_text.send_message_to', {
        name: t('dispatch.job.send_text.all_drivers'),
      }),
    };

    return propsByMode;
  }, [bulkSendSmsToDrivers, mode, sendSmsToAllDrivers, sendSmsToDriver, source]);

  const { control, handleSubmit, setValue, clearErrors } = useForm<SendTextDTO>({
    resolver: yupResolver(sendTextSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { message: '' },
  });

  const onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      event.stopPropagation();

      handleSubmit(async ({ message }) => {
        if (!source || !propsByMode) {
          return;
        }

        if (Array.isArray(source)) {
          const args: BulkSms_Create = { job_ids: source, message };

          await (propsByMode.sendSms as FunctionBulkSendSms)(args);
        } else {
          if (mode === SendTextDialogMode.ORDER_DRIVERS) {
            setIsSendingSmsToOrderDrivers(true);

            try {
              await (propsByMode.sendSms as FunctionSendSms)(source.id, message);
            } finally {
              setIsSendingSmsToOrderDrivers(false);
            }
          } else {
            await (propsByMode.sendSms as FunctionSendSms)(source.id, message);
          }
        }

        toasterStore.push(
          alert(t('dispatch.job.send_text.text_sent'), AlertTypes.success),
        );
        onClose();
      })();
    },
    [mode, onClose, propsByMode, source, toasterStore],
  );

  useEffect(() => {
    if (isOpen) {
      setValue('message', '');
      clearErrors('message');
    }
  }, [clearErrors, isOpen, setValue]);

  if (!propsByMode?.canSendSms) {
    return <></>;
  }

  const dialogTitle = `${propsByMode.sourceFriendlyId ? `${propsByMode.sourceFriendlyId}: ` : ''}${t('actions.send_text')}`;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} preventCloseOnBackdropClick>
      <form noValidate autoComplete="off" onSubmit={onSubmit}>
        <DialogHeader onClose={onClose}>{dialogTitle}</DialogHeader>

        <DialogContent>
          <TextInput
            control={control}
            isRequired
            rows={5}
            name="message"
            placeholder={propsByMode.textFieldPlaceholder}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isSendingSms}
            onClick={onClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton loading={isSendingSms} type="submit">
            {t('actions.send')}
          </SmallLoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
